import {useState} from "react";
import {Button, message, Space, Table, type TableColumnsType, Upload, UploadProps, Modal, Input, Tabs} from "antd";
import {getCookie, getParseMatchActivityImportUrl, importMatchActivity, getActivityMatchImportTemplate} from "../../apiBridge";
import {ActivityCategoryInfo, ActivityMatchInfo} from "../../interface";
import {UploadOutlined, DownloadOutlined} from "@ant-design/icons";
import Highlighter from "react-highlight-words";

const { Search } = Input;

interface ImportField {
    birth_date: string;
    category: string;
    first_name: string;
    last_name: string;
    display_name: string;
    gender: string;
    location: string;
    noc: string;
    team_name: string;
    license_number: string;
    key: string;
    error?: string;
}

interface ImportModalProps {
    activity: ActivityMatchInfo;
    visible: boolean;
    onClose: () => void;
    onSuccess: () => void;
    categories: ActivityCategoryInfo[] | null;
}

const ImportModal: React.FC<ImportModalProps> = ({ activity, visible, onClose, onSuccess, categories }) => {
    const [importStep, setImportStep] = useState('upload');
    const [messageApi, contextHolder] = message.useMessage();
    const [previewItems, setPreviewItems] = useState<ImportField[]>([]);
    const [searchValue, setSearchValue] = useState('');
    const [loadingState, setLoadingState] = useState(false);
    const [hasErrors, setHasErrors] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const handleDownloadTemplate = async () => {
        setDownloading(true);
        try {
            const response = await fetch(getActivityMatchImportTemplate(), {
                headers: {
                    Authorization: "Bearer " + getCookie("token"),
                }
            });
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'activity_import_template.xlsx';
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } else {
                messageApi.error('download failed');
            }
        } catch (error) {
            messageApi.error('download failed');
        } finally {
            setDownloading(false);
        }
    };

    const handleCancel = () => {
        setImportStep('upload');
        setPreviewItems([]);
        setSearchValue('');
        onClose();
    }

    const errorItems = previewItems.filter((item) => Boolean(item.error));
    const validItems = previewItems.filter((item) => !item.error);

    const matchesSearch = (item: ImportField) =>
        Object.values(item).some((value) =>
            value && value.toString().toLowerCase().includes(searchValue.toLowerCase())
        );

    const filteredValidItems = validItems.filter(matchesSearch);
    const filteredErrorItems = errorItems.filter(matchesSearch);

    const columns: TableColumnsType<ImportField> = [
        {
            title: "Display Name",
            key: "display_name",
            dataIndex: "display_name",
            sorter: (a, b) => a.display_name.localeCompare(b.display_name),
            render: (text) => (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchValue]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ),
        },
        {
            title: "Full Name",
            render: (_, record) => (
                <div style={{ 
                    maxWidth: '160px', 
                    whiteSpace: 'normal', 
                    wordBreak: 'break-word' 
                }}>
                    <Highlighter
                        highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                        searchWords={[searchValue]}
                        autoEscape
                        textToHighlight={record.first_name + " " + record.last_name}
                    />
                </div>
            )
        },
        {
            title: "License Number",
            key: "license_number",
            dataIndex: "license_number",
            sorter: (a, b) => a.license_number.localeCompare(b.license_number),
            render: (text) => (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchValue]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ),
        },
        {
            title: "Team",
            key: "team_name",
            dataIndex: "team_name",
            sorter: (a, b) => a.team_name.localeCompare(b.team_name),
            render: (text) => (
                <div style={{ 
                    maxWidth: '200px', 
                    whiteSpace: 'normal', 
                    wordBreak: 'break-word' 
                }}>
                    <Highlighter
                        highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                        searchWords={[searchValue]}
                        autoEscape
                        textToHighlight={text ? text.toString() : ""}
                    />
                </div>
            ),
        },
        {
            title: "Category",
            key: "category",
            dataIndex: "category",
            sorter: (a, b) => a.category.localeCompare(b.category),
            render: (text) => (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchValue]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ),
        },
        {
            title: "Gender",
            key: "gender",
            dataIndex: "gender",
            sorter: (a, b) => a.gender.localeCompare(b.gender),
            render: (text) => (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchValue]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ),
        },
        
        {
            title: "Date of birth",
            render: (_, record) => {
                return record.birth_date
            }
        },
    ];

    const errorColumns: TableColumnsType<ImportField> = [
        {
            title: "Row",
            key: "key",
            dataIndex: "key",
            sorter: (a, b) => a.key.localeCompare(b.key),
            render: (text) => (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchValue]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ),
        },
        {
            title: "Display Name",
            key: "display_name",
            dataIndex: "display_name",
            sorter: (a, b) => a.display_name.localeCompare(b.display_name),
            render: (text) => (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchValue]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ),
        },
        {
            title: "License Number",
            key: "license_number",
            dataIndex: "license_number",
            sorter: (a, b) => a.license_number.localeCompare(b.license_number),
            render: (text) => (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchValue]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ),
        },
        {
            title: "Category",
            key: "category",
            dataIndex: "category",
            sorter: (a, b) => a.category.localeCompare(b.category),
            render: (text) => (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchValue]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ),
        },
        {
            title: "Gender",
            key: "gender",
            dataIndex: "gender",
            sorter: (a, b) => a.gender.localeCompare(b.gender),
            render: (text) => (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchValue]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ),
        },
        {
            title: "Date of birth",
            render: (_, record) => (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchValue]}
                    autoEscape
                    textToHighlight={record.birth_date ? record.birth_date.toString() : ""}
                />
            )
        },
        {
            title: "Error",
            key: "error",
            dataIndex: "error",
            render: (text) => (
                <span style={{ color: 'red' }}>{text}</span>
            ),
        }
    ];

    const doImport = async () => {
        let inputValue = '';
        const hasCategories = categories && categories.length > 0;
        
        Modal.confirm({
            title: 'Confirm Import',
            content: hasCategories ? (
                <div>
                    <p>This action will:</p>
                    <ul>
                        <li>Overwrite all participant data</li>
                        <li>Overwrite all category information</li>
                        <li>Reset all related match data</li>
                    </ul>
                    {hasErrors && (
                        <div style={{ color: 'red', margin: '10px 0' }}>
                            <p>Warning: {errorItems.length} items with errors will be skipped during import.</p>
                        </div>
                    )}
                    <p>This operation cannot be undone.</p>
                    <p>Current activity name: <strong>{activity.name}</strong></p>
                    <p>Please enter the activity name to confirm:</p>
                    <Input 
                        onChange={(e) => inputValue = e.target.value}
                        placeholder="Enter activity name"
                    />
                </div>
            ) : (
                <div>
                    <p>This action will create categories based on the imported data.</p>
                    {hasErrors && (
                        <div style={{ color: 'red', margin: '10px 0' }}>
                            <p>Warning: {errorItems.length} items with errors will be skipped during import.</p>
                        </div>
                    )}
                    <p>Are you sure you want to proceed?</p>
                </div>
            ),
            okText: 'Confirm Import',
            cancelText: 'Cancel',
            onOk: async () => {
                if (hasCategories) {
                    if (!inputValue) {
                        messageApi.error('Please enter activity name');
                        return false;
                    }
                    if (inputValue !== activity.name) {
                        messageApi.error('Activity name does not match');
                        return false;
                    }
                }
                setLoadingState(true);
                try {
                    // Filter out items with errors before sending
                    const validDataToImport = previewItems.filter((item) => !item.error);
                    const res = await importMatchActivity(activity.id, {data: validDataToImport});
                    const resp = await res.json().catch(() => null);
                    if (!res.ok || resp?.error) {
                        messageApi.error(resp?.error || `Import failed (${res.status})`);
                        return false;
                    }

                    setPreviewItems([]);
                    messageApi.success("Import successful");
                    onSuccess();
                    onClose();
                    return true;
                } catch (error) {
                    messageApi.error(
                        error instanceof Error ? error.message : "Import request failed",
                    );
                    return false;
                } finally {
                    setLoadingState(false);
                }
            }
        });
    }

    const uploadHeaders = {
      Authorization: "",
      // antd adds this header by default; null keeps the existing CORS workaround.
      "X-Requested-With": null as unknown as string,
    };
    const uploadProps: UploadProps = {
        action: getParseMatchActivityImportUrl(activity.id),
        showUploadList: false,
        method: "POST",
        accept: ".csv,.xlsx",
        maxCount: 1,
        beforeUpload: () => {
            uploadHeaders.Authorization = "Bearer " + getCookie("token");
            return true;
        },
        headers: uploadHeaders,
        onChange: function(e) {
            const resp = e.file.response as { error?: string; data?: ImportField[] } | undefined;
            if (!resp) {
                return;
            }

            if (resp.error) {
                messageApi.error(resp.error)
                return;
            }

            const rows = Array.isArray(resp.data) ? resp.data : [];
            setPreviewItems(rows);
            setHasErrors(rows.some((item) => Boolean(item.error)));
            setImportStep('preview')
        }
    }

    return (
        <Modal
            title="Import Participants"
            open={visible}
            onCancel={handleCancel}
            footer={null}
            width={importStep === 'preview' ? 1200 : 480}
            maskClosable={false}
        >
            {contextHolder}
            {importStep === 'upload' ? (
                <div>
                    <Space>
                        <Upload {...uploadProps}>
                            <Button type="primary" icon={<UploadOutlined />}>Upload</Button>
                        </Upload>
                        <Button 
                            type="link"
                            icon={<DownloadOutlined />}
                            onClick={handleDownloadTemplate}
                            loading={downloading}
                        >
                            Download Template
                        </Button>
                    </Space>
                    <div style={{ marginTop: 8, color: 'rgba(0, 0, 0, 0.45)' }}>
                        Supported formats: .xlsx, .csv
                    </div>
                    <div style={{ marginTop: 8 }}>
                        <Button onClick={handleCancel}>Close</Button>
                    </div>
                </div>
            ) : null}

            {importStep === 'preview' ? (
                <>
                    <Search
                        placeholder="Search..."
                        allowClear
                        onChange={(e) => setSearchValue(e.target.value)}
                        style={{ marginBottom: 16 }}
                    />
                    <Tabs
                        items={[
                            {
                                key: 'valid',
                                label: 'Valid Data',
                                children: (
                                    <Table
                                        columns={columns}
                                        dataSource={filteredValidItems}
                                        rowKey="key"
                                        scroll={{
                                            x: "max-content",
                                        }}
                                        size="small"
                                    />
                                ),
                            },
                            {
                                key: 'error',
                                label: `Error Data (${filteredErrorItems.length})`,
                                children: (
                                    <Table
                                        columns={errorColumns}
                                        dataSource={filteredErrorItems}
                                        rowKey="key"
                                        scroll={{
                                            x: "max-content",
                                        }}
                                        size="small"
                                    />
                                ),
                            },
                        ]}
                    />

                    <Space style={{ marginTop: 20 }}>
                        <Button loading={loadingState} type="primary" onClick={() => doImport()}>Import</Button>
                        <Button onClick={handleCancel}>Cancel</Button>
                    </Space>
                </>
            ) : null}
        </Modal>
    );
};

export default ImportModal;
